import React, { useState, useEffect } from 'react';
import { getTheme } from '../theme';
import { API, fmtDate, fmtPhone, authHeaders, shopHoursText } from '../utils/helpers';
import SmsConversation from './SmsConversation';
import { useTextingReady } from '../utils/texting';

export default function PickupWizard({
  ticket, onClose, onNext, onPrev, currentIndex, totalCount,
  onLogUpdated, onSendBack, theme = 'dark', currentUser = null
}) {
  const isDark = theme === 'dark';
  const T = getTheme(theme);

  const [detail, setDetail]             = useState(null);
  const [loading, setLoading]           = useState(true);
  const [contactLog, setContactLog]     = useState([]);
  const [shopProfile, setShopProfile]   = useState({});
  const textingReady = useTextingReady();
  const [activeTab, setActiveTab]       = useState('sms');
  const [emailTo, setEmailTo]           = useState('');
  const [emailSubject, setEmailSubject] = useState('');
  const [emailBody, setEmailBody]       = useState('');
  const [sendingEmail, setSendingEmail] = useState(false);
  const [employees, setEmployees]           = useState([]);
  const [showSendBack, setShowSendBack]     = useState(false);
  const [sendBackNote, setSendBackNote]     = useState('');
  const [sendBackTechId, setSendBackTechId] = useState('');
  const [sendingBack, setSendingBack]       = useState(false);
  const [markingPickedUp, setMarkingPickedUp] = useState(false);
  const [statusMeta, setStatusMeta]     = useState([]);
  const [toast, setToast]               = useState(null);
  const [showAllContacts, setShowAllContacts] = useState(false);

  // Line subtotals arrive from the server already net of their line discount,
  // so the Subtotal row is shown gross and the saving gets its own row — the
  // same breakdown the Lightspeed invoice copy shows. Warranty lines are not
  // charged at all, so they contribute nothing.
  const discountTotal = (detail?.lineItems || []).reduce(
    (sum, l) => sum + (l.warranty ? 0 : parseFloat(l.discountAmount || 0)), 0);
  const [callOutcome, setCallOutcome]   = useState('');
  const [callNote, setCallNote]         = useState('');
  const [callPromise, setCallPromise]   = useState('');
  const [loggingCall, setLoggingCall]   = useState(false);

  // Open on a channel that can actually reach this customer. The tab defaulted
  // to Text for everyone, so a landline-only customer landed on the one surface
  // that could do nothing for them — half of why logging a call felt impossible
  // rather than merely absent. Resets per ticket, since the wizard navigates
  // between them with the arrow keys.
  useEffect(() => {
    if (!ticket) return;
    const canText = textingReady && ticket.phone && String(ticket.phoneType || '').toLowerCase() === 'mobile';
    setActiveTab(canText ? 'sms' : ticket.email ? 'email' : 'call');
    setCallOutcome(''); setCallNote(''); setCallPromise('');
  }, [ticket?.id, ticket?.phone, ticket?.phoneType, ticket?.email, textingReady]);

  const showToast = (message, ok = true) => {
    setToast({ message, ok });
    setTimeout(() => setToast(null), 4000);
  };

  const buildEmailBody = (det, t, profile) => {
    const firstName = (t.customer || '').split(' ')[0];
    const lines = [];
    lines.push(`Hi ${firstName},`);
    lines.push('');
    lines.push(`Great news — your ${t.bike} is ready for pickup!`);
    lines.push('');
    if (det?.lineItems?.length > 0) {
      lines.push('SERVICE SUMMARY');
      lines.push('----------------------------------------');
      det.lineItems.forEach((item, i) => {
        const qty = item.quantity > 1 ? ` x${item.quantity}` : '';
        lines.push(`${item.description}${qty}`);
        if (item.note) lines.push(item.note);
        lines.push(`$${(item.subtotal || 0).toFixed(2)}`);
      });
      lines.push('');
      lines.push('----------------------------------------');
      // det.subtotal is net of line discounts. Show the gross and break the
      // discount out, so the customer sees what they saved the same way the
      // Lightspeed invoice copy shows it.
      const msgDisc = det.lineItems.reduce((sum, l) => sum + (l.warranty ? 0 : parseFloat(l.discountAmount || 0)), 0);
      lines.push(`Subtotal:                        $${((det.subtotal || 0) + msgDisc).toFixed(2)}`);
      if (msgDisc > 0.004) {
        lines.push(`Discount:                       -$${msgDisc.toFixed(2)}`);
      }
      if (det.taxRate > 0) {
        lines.push(`Est. Tax (${det.taxRate}%):              $${(det.taxAmount || 0).toFixed(2)}`);
      }
      lines.push(`Est. Total:                      $${(det.totalValue || 0).toFixed(2)}`);
      lines.push('');
    }
    if (t.isPaid) {
      lines.push('Your service is paid in full — just come grab your bike!');
    } else {
      lines.push(`Payment of $${(det?.totalValue || 0).toFixed(2)} is due at pickup.`);
    }
    lines.push('');
    lines.push('PICKUP INFORMATION');
    if (profile.name)    lines.push(profile.name);
    if (profile.address) lines.push(profile.address);
    if (profile.phone)   lines.push(profile.phone);
    if (shopHoursText(profile)) lines.push(`Hours: ${shopHoursText(profile)}`);
    lines.push('');
    lines.push('Questions? Reply to this email or give us a call. We look forward to seeing you!');
    lines.push('');
    if (profile.signature) lines.push(profile.signature);
    else if (profile.name) lines.push(`${profile.name} Service Team`);
    return lines.join('\n');
  };

  useEffect(() => {
    if (!ticket) return;
    setLoading(true);
    setDetail(null);
    setShowSendBack(false);
    setSendBackNote('');
    setToast(null);

    Promise.all([
      fetch(`${API}/api/tickets/${ticket.id}`, { credentials: 'include', headers: authHeaders() }).then(r => r.json()),
      fetch(`${API}/api/contact-log/${ticket.id}`, { credentials: 'include', headers: authHeaders() }).then(r => r.json()),
      fetch(`${API}/api/settings`, { credentials: 'include', headers: authHeaders() }).then(r => r.json()),
      fetch(`${API}/api/tickets/status-meta`, { credentials: 'include', headers: authHeaders() }).then(r => r.json()).catch(() => []),
    ]).then(([det, log, settings, meta]) => {
      setDetail(det);
      setContactLog(Array.isArray(log) ? log : []);
      if (Array.isArray(meta)) setStatusMeta(meta);
      const profile = settings?.shopProfile || {};
      setShopProfile(profile);
      setEmailTo(ticket.email || '');
      setEmailSubject(`Your ${ticket.bike} is ready for pickup!`);
      setEmailBody(buildEmailBody(det, ticket, profile));
      setLoading(false);
    }).catch(() => setLoading(false));
  }, [ticket?.id]);

  useEffect(() => {
    fetch(`${API}/api/tickets/employees/list`, { credentials: 'include', headers: authHeaders() })
      .then(r => r.json())
      .then(data => { if (Array.isArray(data)) setEmployees(data); })
      .catch(() => {});
  }, []);

  useEffect(() => {
    const handler = (e) => {
      if (showSendBack) return;
      if (e.key === 'Escape') onClose();
      if (e.key === 'ArrowLeft'  && currentIndex > 0)              onPrev();
      if (e.key === 'ArrowRight' && currentIndex < totalCount - 1) onNext();
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [onClose, onPrev, onNext, currentIndex, totalCount, showSendBack]);

  const updateContactSummary = (method, outcome = 'sent') => {
    const entry = {
      id: Date.now(), method, outcome,
      timestamp: new Date().toISOString(),
      sentByName: currentUser?.first_name
        ? `${currentUser.first_name} ${currentUser.last_name || ''}`.trim()
        : 'Staff',
    };
    // Prepend to the wizard's own local contactLog so the "Contact History"
    // list in the left panel grows immediately too — previously it only
    // reflected the ticket's history as of when the wizard first opened,
    // and never updated again until the wizard was closed and reopened.
    setContactLog(prev => [entry, ...prev]);
    if (onLogUpdated) {
      // Pass the FULL updated history, not just this one new entry. The
      // parent (PickupPage) derives its "Xx contacted" badge count from
      // this array's length — passing only the new entry silently reset
      // that badge to "1x contacted" every time a contact was logged from
      // inside the wizard, regardless of how many prior contacts existed.
      onLogUpdated(ticket.id, [entry, ...contactLog]);
    }
  };

  const handleSendEmail = async () => {
    if (!emailTo.trim() || !emailBody.trim()) return;
    setSendingEmail(true);
    try {
      const token = localStorage.getItem('sb_token');
      const res = await fetch(`${API}/api/contact-log/send-email`, {
        method: 'POST', credentials: 'include',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ to: emailTo, subject: emailSubject, body: emailBody, ticketId: ticket.id }),
      });
      if (res.ok) {
        showToast(`Email sent to ${emailTo}`);
        updateContactSummary('📧 Emailed');
        window.dispatchEvent(new CustomEvent('spinboard-contact-updated', { detail: { ticketId: ticket.id } }));
      } else throw new Error('Send failed');
    } catch (err) { showToast(`Email failed: ${err.message}`, false); }
    setSendingEmail(false);
  };

  const handleSendBack = async () => {
    setSendingBack(true);
    try {
      const token = localStorage.getItem('sb_token');
      const openStatus = statusMeta.find(s => (s.name || '').toLowerCase() === 'open');
      if (!openStatus) { showToast('Could not find Open status — check Settings', false); setSendingBack(false); return; }
      await fetch(`${API}/api/tickets/${ticket.id}/status`, {
        method: 'PUT', credentials: 'include',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ workorderStatusID: openStatus.workorderStatusID, clearCompletion: true }),
      });
      if (sendBackNote.trim()) {
        const now = new Date();
        const userName = currentUser?.first_name
          ? `${currentUser.first_name} ${currentUser.last_name || ''}`.trim()
          : (currentUser?.username || 'Staff');
        const stamp = `[Sent Back — ${userName} — ${now.toLocaleDateString('en-US',{month:'short',day:'numeric',year:'numeric'})} ${now.toLocaleTimeString('en-US',{hour:'numeric',minute:'2-digit'})}]`;
        const existing = detail?.internalNote?.trim();
        const combined = `${stamp}\n${sendBackNote.trim()}${existing ? '\n\n' + existing : ''}`;
        await fetch(`${API}/api/tickets/${ticket.id}/internal-note`, {
          method: 'PUT', credentials: 'include',
          headers: authHeaders(true),
          body: JSON.stringify({ note: combined }),
        });
      }
      if (sendBackTechId) {
        await fetch(`${API}/api/tickets/${ticket.id}/tech`, {
          method: 'PUT', credentials: 'include',
          headers: authHeaders(true),
          body: JSON.stringify({ employeeID: sendBackTechId }),
        });
      }
      showToast('Ticket sent back to service queue');
      setTimeout(() => { onSendBack && onSendBack(); onClose(); }, 1500);
    } catch (err) { showToast(`Failed: ${err.message}`, false); setSendingBack(false); }
  };

  // Logging a call needs no new backend: POST /api/contact-log/:ticketId takes
  // { method, outcome, note } and never cared which channel was used — the gap
  // was only that the wizard's two tabs both required one. A customer with a
  // landline, or none at all, was unreachable through the UI while being
  // perfectly loggable underneath.
  const handleLogCall = async () => {
    if (!callOutcome) return;
    setLoggingCall(true);
    try {
      await fetch(`${API}/api/contact-log/${ticket.id}`, {
        method: 'POST', credentials: 'include',
        headers: authHeaders(true),
        body: JSON.stringify({ method: '📞 Called', outcome: callOutcome, note: callNote.trim() }),
      });

      // Only offered on "Spoke with customer", and written separately so a
      // failure here cannot lose the call log — the conversation happened
      // whether or not the date saved. Matches the T17:00 convention used by
      // ticket creation so promise dates all land at the same hour.
      let promiseSaved = false;
      if (callOutcome === 'Spoke with customer' && callPromise) {
        try {
          const r = await fetch(`${API}/api/tickets/${ticket.id}/fields`, {
            method: 'PUT', credentials: 'include',
            headers: authHeaders(true),
            body: JSON.stringify({ etaOut: `${callPromise}T17:00:00+00:00` }),
          });
          promiseSaved = r.ok;
        } catch (_) { promiseSaved = false; }
      }

      updateContactSummary('📞 Called', callOutcome);
      window.dispatchEvent(new CustomEvent('spinboard-contact-updated', { detail: { ticketId: ticket.id } }));
      showToast(
        callPromise && callOutcome === 'Spoke with customer'
          ? (promiseSaved ? 'Call logged and promise date set' : 'Call logged — promise date did not save')
          : 'Call logged',
        !(callPromise && callOutcome === 'Spoke with customer' && !promiseSaved)
      );
      setCallOutcome(''); setCallNote(''); setCallPromise('');
      if (promiseSaved) onSendBack && onSendBack();
    } catch (err) { showToast(`Could not log the call: ${err.message}`, false); }
    setLoggingCall(false);
  };

  const handlePickedUp = async () => {
    setMarkingPickedUp(true);
    try {
      const token = localStorage.getItem('sb_token');
      await fetch(`${API}/api/contact-log/${ticket.id}`, {
        method: 'POST', credentials: 'include',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ method: '🚶 Customer came in', outcome: 'Picked up bike', note: '' }),
      });
      showToast('Marked as picked up! Close the ticket in Lightspeed when ready.');
      updateContactSummary('🚶 Customer came in');
      setTimeout(() => onClose(), 2500);
    } catch (err) { showToast(`Failed: ${err.message}`, false); }
    setMarkingPickedUp(false);
  };

  if (!ticket) return null;

  const bg       = isDark ? '#0f1724' : '#f4f4ef';
  const panelBg  = isDark ? '#1a2336' : '#ffffff';
  const border   = isDark ? '#2e3d58' : '#e0e0d8';
  const textPri  = T.text;
  const textSub  = T.textSub;
  const itemBg   = isDark ? '#243050' : '#f0f0e8';
  const inputBg  = isDark ? '#243050' : '#f4f4ef';
  const inputBdr = isDark ? '#2e3d58' : '#d0d0c8';
  const noteBg   = isDark ? '#1e2d44' : '#f8f8f4';
  const urgColor = (d) => d > 7 ? '#e74c3c' : d > 3 ? '#e67e22' : '#2ecc71';

  return (
    <div
      style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.75)', zIndex:9000,
        display:'flex', alignItems:'center', justifyContent:'center', padding:16 }}
      onClick={onClose}
    >
      <div
        style={{ background:bg, borderRadius:14, width:'100%', maxWidth:1200,
          maxHeight:'95vh', minWidth:700, minHeight:500, display:'flex', flexDirection:'column',
          boxShadow:'0 24px 80px rgba(0,0,0,0.5)', overflow:'hidden',
          border:`1px solid ${border}`, position:'relative', resize:'both' }}
        onClick={e => e.stopPropagation()}
      >

        {/* Header */}
        <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between',
          padding:'16px 24px', borderBottom:`1px solid ${border}`, background:panelBg, flexShrink:0 }}>
          <div style={{ display:'flex', alignItems:'center', gap:10 }}>
            <span style={{ fontSize:17, fontWeight:700, color:textPri, fontFamily:"'IBM Plex Mono',monospace" }}>
              {ticket.displayId}
            </span>
            <span style={{ fontSize:11, fontWeight:700, padding:'3px 10px', borderRadius:20,
              background: ticket.isPaid?(isDark?'#0f2a15':'#d4edda'):(isDark?'#2a1f0a':'#fff3e0'),
              color: ticket.isPaid?'#2ecc71':'#e67e22' }}>
              {ticket.isPaid ? '✓ Paid' : 'Unpaid'}
            </span>
            <span style={{ fontSize:12, fontWeight:600, padding:'3px 10px', borderRadius:20,
              background:isDark?'#1a2e1a':'#e8f5e9', color:urgColor(ticket.daysFinished) }}>
              {ticket.daysFinished}d waiting
            </span>
            {ticket.isOverdue && (
              <span style={{ fontSize:11, padding:'3px 10px', borderRadius:20,
                background:isDark?'#2a0f0f':'#fde8e8', color:'#e74c3c', fontWeight:600 }}>
                {Math.abs(ticket.daysUntilDue)}d overdue
              </span>
            )}
          </div>
          <div style={{ display:'flex', alignItems:'center', gap:8 }}>
            <span style={{ fontSize:12, color:textSub }}>{currentIndex + 1} / {totalCount}</span>
            <button onClick={onPrev} disabled={currentIndex === 0}
              style={{ background:itemBg, border:`1px solid ${border}`, borderRadius:6, width:30, height:30,
                fontSize:18, cursor:currentIndex===0?'default':'pointer',
                color:currentIndex===0?textSub:textPri, display:'flex', alignItems:'center', justifyContent:'center' }}>‹</button>
            <button onClick={onNext} disabled={currentIndex === totalCount - 1}
              style={{ background:itemBg, border:`1px solid ${border}`, borderRadius:6, width:30, height:30,
                fontSize:18, cursor:currentIndex===totalCount-1?'default':'pointer',
                color:currentIndex===totalCount-1?textSub:textPri, display:'flex', alignItems:'center', justifyContent:'center' }}>›</button>
            <button onClick={onClose}
              style={{ background:'transparent', border:'none', color:textSub, cursor:'pointer', fontSize:24, lineHeight:1, padding:'2px 6px' }}>×</button>
          </div>
        </div>

        {/* Body */}
        <div style={{ display:'flex', flex:1, minHeight:0, overflow:'hidden' }}>

          {/* Left panel */}
          <div style={{ width:'44%', borderRight:`1px solid ${border}`, overflowY:'auto',
            padding:'20px 24px', display:'flex', flexDirection:'column', gap:18 }}>

            {loading ? (
              <div style={{ color:textSub, fontSize:13, textAlign:'center', paddingTop:48 }}>Loading ticket details…</div>
            ) : (
              <>
                {/* Customer & contact */}
                <div>
                  <div style={{ fontSize:11, color:textSub, textTransform:'uppercase', letterSpacing:'0.06em', marginBottom:8 }}>Customer & Contact</div>
                  <div style={{ background:itemBg, borderRadius:8, padding:'14px 16px' }}>
                    <div style={{ fontSize:16, fontWeight:700, color:textPri, marginBottom:2 }}>{ticket.customer}</div>
                    <div style={{ fontSize:13, color:textSub, marginBottom:10 }}>{ticket.bike}</div>
                    <div style={{ display:'flex', flexDirection:'column', gap:5 }}>
                      {ticket.phone ? (
                        <a href={`tel:${ticket.phone}`} style={{ fontSize:13, color:'#3498db', textDecoration:'none', fontWeight:500 }}>
                          📞 {fmtPhone(ticket.phone)}
                          {ticket.phoneType && <span style={{ fontSize:11, color:textSub, fontWeight:400, marginLeft:6 }}>{ticket.phoneType}</span>}
                        </a>
                      ) : (
                        <span style={{ fontSize:12, color:textSub, fontStyle:'italic' }}>No phone on file</span>
                      )}
                      {ticket.email ? (
                        <span style={{ fontSize:13, color:textSub }}>✉ {ticket.email}</span>
                      ) : (
                        <span style={{ fontSize:12, color:'#e67e22', fontStyle:'italic' }}>No email on file</span>
                      )}
                    </div>
                  </div>
                </div>

                {/* Dates */}
                <div>
                  <div style={{ fontSize:11, color:textSub, textTransform:'uppercase', letterSpacing:'0.06em', marginBottom:8 }}>Dates</div>
                  <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:8 }}>
                    <div style={{ background:itemBg, borderRadius:8, padding:'10px 12px' }}>
                      <div style={{ fontSize:10, color:textSub, marginBottom:3 }}>COMPLETED</div>
                      <div style={{ fontSize:13, color:textPri }}>{fmtDate(ticket.timeIn)}</div>
                    </div>
                    {ticket.etaOut && (
                      <div style={{ background:itemBg, borderRadius:8, padding:'10px 12px' }}>
                        <div style={{ fontSize:10, color:textSub, marginBottom:3 }}>PROMISE DATE</div>
                        <div style={{ fontSize:13, color:ticket.isOverdue?'#e74c3c':ticket.daysUntilDue<=2?'#e67e22':textPri }}>
                          {fmtDate(ticket.etaOut)}
                        </div>
                      </div>
                    )}
                  </div>
                </div>

                {/* Receipt note */}
                {ticket.note && (
                  <div>
                    <div style={{ fontSize:11, color:textSub, textTransform:'uppercase', letterSpacing:'0.06em', marginBottom:8 }}>Receipt Note</div>
                    <div style={{ background:noteBg, borderRadius:8, padding:'10px 14px', fontSize:13, color:textPri, lineHeight:1.55 }}>
                      {ticket.note}
                    </div>
                  </div>
                )}

                {/* Line items */}
                {detail?.lineItems?.length > 0 && (
                  <div>
                    <div style={{ fontSize:11, color:textSub, textTransform:'uppercase', letterSpacing:'0.06em', marginBottom:8 }}>Line Items</div>
                    <div style={{ display:'flex', flexDirection:'column', gap:3 }}>
                      {detail.lineItems.map((item, i) => {
                        const isPart = item.type === 'inventory';
                        return (
                          <div key={i} style={{ display:'flex', alignItems:'center', gap:8, padding:'6px 10px', borderRadius:6, background:noteBg }}>
                            <span style={{ fontSize:10, fontWeight:700, padding:'2px 6px', borderRadius:3, flexShrink:0,
                              background:isPart?(isDark?'#0d2218':'#f0fdf4'):(isDark?'#0d1e2e':'#eff6ff'),
                              color:isPart?'#2ecc71':'#3498db' }}>
                              {isPart ? 'Parts' : 'Labor & Misc'}
                            </span>
                            <div style={{ flex:1 }}>
                              <div style={{ fontSize:12, color:textPri }}>{item.description}</div>
                              {item.note && <div style={{ fontSize:11, color:textSub, marginTop:2, lineHeight:1.4 }}>{item.note}</div>}
                            </div>
                            {item.quantity > 1 && <span style={{ fontSize:11, color:textSub }}>×{item.quantity}</span>}
                            <span style={{ fontSize:12, fontFamily:"'IBM Plex Mono',monospace", color:textPri, fontWeight:500 }}>
                              ${(item.subtotal || 0).toFixed(2)}
                            </span>
                          </div>
                        );
                      })}
                      <div style={{ display:'flex', flexDirection:'column', alignItems:'flex-end', gap:2,
                        paddingTop:8, marginTop:4, borderTop:`1px solid ${border}` }}>
                        <span style={{ fontSize:11, color:textSub, fontFamily:"'IBM Plex Mono',monospace" }}>
                          Subtotal: ${((detail.subtotal || 0) + discountTotal).toFixed(2)}
                        </span>
                        {discountTotal > 0.004 && (
                          <span style={{ fontSize:11, color:'#2ecc71', fontFamily:"'IBM Plex Mono',monospace" }}>
                            Discount: -${discountTotal.toFixed(2)}
                          </span>
                        )}
                        {detail.taxRate > 0 ? (
                          <span style={{ fontSize:11, color:textSub, fontFamily:"'IBM Plex Mono',monospace" }}>
                            Tax ({detail.taxRate}%): ${(detail.taxAmount || 0).toFixed(2)}
                          </span>
                        ) : (
                          <span style={{ fontSize:11, color:textSub, fontStyle:'italic' }}>Tax calculated at checkout</span>
                        )}
                        <span style={{ fontSize:14, fontWeight:700, color:textPri, fontFamily:"'IBM Plex Mono',monospace" }}>
                          Total: ${(detail.totalValue || 0).toFixed(2)}
                        </span>
                      </div>
                    </div>
                  </div>
                )}

                {/* Contact history */}
                {contactLog.length > 0 && (
                  <div>
                    <div style={{ fontSize:11, color:textSub, textTransform:'uppercase', letterSpacing:'0.06em', marginBottom:8 }}>
                      Contact History ({contactLog.length})
                    </div>
                    <div style={{ display:'flex', flexDirection:'column', gap:4,
                      maxHeight: showAllContacts ? 400 : 'none', overflowY: showAllContacts ? 'auto' : 'visible' }}>
                      {(showAllContacts ? contactLog : contactLog.slice(0, 5)).map(a => (
                        <div key={a.id} style={{ background:itemBg, borderRadius:6, padding:'8px 10px',
                          display:'flex', alignItems:'flex-start', justifyContent:'space-between', gap:8 }}>
                          <div>
                            <div style={{ fontSize:12, color:textPri, fontWeight:500 }}>
                              {a.method}
                              <span style={{ fontSize:11, color:textSub, fontWeight:400 }}> · {a.outcome}</span>
                              {a.sentByName && <span style={{ fontSize:11, color:textSub }}> · {a.sentByName}</span>}
                            </div>
                            {a.note && <div style={{ fontSize:11, color:textSub, marginTop:2 }}>{a.note}</div>}
                          </div>
                          <div style={{ fontSize:10, color:textSub, whiteSpace:'nowrap', flexShrink:0 }}>
                            {new Date(a.timestamp).toLocaleDateString('en-US',{month:'short',day:'numeric'})}
                          </div>
                        </div>
                      ))}
                    </div>
                    {contactLog.length > 5 && (
                      <button onClick={() => setShowAllContacts(v => !v)}
                        style={{ width:'100%', marginTop:6, padding:'6px', background:'transparent',
                          border:'none', color:'#3498db', fontSize:11, cursor:'pointer', fontFamily:"'Inter',sans-serif" }}>
                        {showAllContacts ? 'Show fewer entries' : `+${contactLog.length - 5} more entries — click to view all`}
                      </button>
                    )}
                  </div>
                )}
              </>
            )}
          </div>

          {/* Right panel — Compose */}
          <div style={{ flex:1, display:'flex', flexDirection:'column', overflow:'hidden' }}>

            {/* Tab bar */}
            <div style={{ display:'flex', borderBottom:`1px solid ${border}`, background:panelBg, flexShrink:0 }}>
              {/* Ordered by how often the shop actually uses them: text first,
                  then email, then call as the fallback for customers the other
                  two cannot reach. */}
              {[{k:'sms',l:'💬  Text'},{k:'email',l:'✉  Email'},{k:'call',l:'📞  Call'}].map(tab => (
                <button key={tab.k} onClick={() => setActiveTab(tab.k)}
                  style={{ flex:1, padding:'14px', fontSize:13, fontWeight:600, background:'transparent', border:'none',
                    borderBottom: activeTab===tab.k ? '2px solid #3498db' : '2px solid transparent',
                    color: activeTab===tab.k ? textPri : textSub,
                    cursor:'pointer', fontFamily:"'Inter',sans-serif", letterSpacing:'0.03em' }}>
                  {tab.l}
                </button>
              ))}
            </div>

            {/* Tab body */}
            <div style={{ flex:1, minHeight:0, overflow:'hidden', padding:'20px 24px', display:'flex', flexDirection:'column' }}>

              {activeTab === 'email' && (
                <div style={{ flex:1, minHeight:0, overflowY:'auto', display:'flex', flexDirection:'column', gap:14 }}>
                  <div>
                    <div style={{ fontSize:11, color:textSub, textTransform:'uppercase', letterSpacing:'0.06em', marginBottom:6 }}>To</div>
                    <input
                      value={emailTo}
                      onChange={e => setEmailTo(e.target.value)}
                      placeholder="Enter email address…"
                      style={{ width:'100%', background:inputBg,
                        border:`1px solid ${emailTo.trim() ? inputBdr : '#e74c3c'}`,
                        borderRadius:7, padding:'8px 12px', color:textPri, fontSize:13,
                        fontFamily:"'Inter',sans-serif", outline:'none', boxSizing:'border-box' }}
                    />
                    {!emailTo.trim() && !ticket.email && (
                      <div style={{ fontSize:11, color:'#e67e22', marginTop:4 }}>
                        No email on file — enter an address above to send
                      </div>
                    )}
                  </div>
                  <div>
                    <div style={{ fontSize:11, color:textSub, textTransform:'uppercase', letterSpacing:'0.06em', marginBottom:6 }}>Subject</div>
                    <input
                      value={emailSubject}
                      onChange={e => setEmailSubject(e.target.value)}
                      style={{ width:'100%', background:inputBg, border:`1px solid ${inputBdr}`,
                        borderRadius:7, padding:'8px 12px', color:textPri, fontSize:13,
                        fontFamily:"'Inter',sans-serif", outline:'none', boxSizing:'border-box' }}
                    />
                  </div>
                  <div style={{ display:'flex', flexDirection:'column', flex:1 }}>
                    <div style={{ fontSize:11, color:textSub, textTransform:'uppercase', letterSpacing:'0.06em', marginBottom:6 }}>Message</div>
                    <textarea
                      value={emailBody}
                      onChange={e => setEmailBody(e.target.value)}
                      style={{ flex:1, minHeight:240, width:'100%', background:inputBg, border:`1px solid ${inputBdr}`,
                        borderRadius:7, padding:'10px 12px', color:textPri, fontSize:12,
                        fontFamily:'monospace', outline:'none', resize:'vertical', boxSizing:'border-box', lineHeight:1.6 }}
                    />
                  </div>
                  <button onClick={handleSendEmail}
                    disabled={sendingEmail || !emailTo.trim() || !emailBody.trim()}
                    style={{ padding:'11px', borderRadius:8, border:'none', fontSize:14, fontWeight:600,
                      cursor: sendingEmail || !emailTo.trim() ? 'not-allowed' : 'pointer',
                      fontFamily:"'Inter',sans-serif",
                      background: sendingEmail || !emailTo.trim() ? (isDark?'#243050':'#d0d0c8') : '#3498db',
                      color: sendingEmail || !emailTo.trim() ? textSub : '#fff' }}>
                    {sendingEmail ? 'Sending…' : '✉ Send Email'}
                  </button>
                </div>
              )}

              {activeTab === 'sms' && (
                <SmsConversation
                  phone={ticket.phone}
                  ticketId={ticket.id}
                  customerId={detail?.customer?.id}
                  customerName={ticket.customer}
                  bikeLabel={ticket.bike}
                  balance={detail?.totalValue || 0}
                  ticketStatusCategory={detail?.statusCategory || null}
                  theme={theme}
                  markReadOnOpen={true}
                  onSent={(method) => updateContactSummary(method)}
                />
              )}

              {activeTab === 'call' && (
                <div style={{ flex:1, minHeight:0, overflowY:'auto', display:'flex', flexDirection:'column', gap:16 }}>
                  {/* Always available, not conditional on the absence of a mobile:
                      you might phone someone who also has one. The number is shown
                      with its type because a landline is exactly the case this tab
                      exists for. */}
                  <div>
                    <div style={{ fontSize:11, color:textSub, textTransform:'uppercase', letterSpacing:'0.06em', marginBottom:6 }}>Number on file</div>
                    <div style={{ fontSize:18, fontWeight:600, color:textPri, fontFamily:"'Inter',sans-serif" }}>
                      {ticket.phone ? fmtPhone(ticket.phone) : 'No phone number on file'}
                      {ticket.phone && ticket.phoneType && (
                        <span style={{ fontSize:12, color:textSub, fontWeight:400, marginLeft:8 }}>{ticket.phoneType}</span>
                      )}
                    </div>
                    {!ticket.phone && (
                      <div style={{ fontSize:11, color:'#e67e22', marginTop:4 }}>
                        You can still log the attempt — add a note saying how you reached them.
                      </div>
                    )}
                  </div>

                  <div>
                    <div style={{ fontSize:11, color:textSub, textTransform:'uppercase', letterSpacing:'0.06em', marginBottom:8 }}>How did it go?</div>
                    <div style={{ display:'flex', flexDirection:'column', gap:8 }}>
                      {['No answer', 'Left voicemail', 'Spoke with customer', 'Wrong or disconnected number'].map(o => (
                        <button key={o} onClick={() => setCallOutcome(o)}
                          style={{ textAlign:'left', padding:'11px 14px', borderRadius:8, fontSize:13.5,
                            cursor:'pointer', fontFamily:"'Inter',sans-serif",
                            background: callOutcome===o ? (isDark ? '#12243a' : '#dbeafe') : 'transparent',
                            border: `1px solid ${callOutcome===o ? '#3498db' : border}`,
                            color: callOutcome===o ? (isDark ? '#60a5fa' : '#1d4ed8') : textPri,
                            fontWeight: callOutcome===o ? 600 : 400 }}>
                          {o}
                        </button>
                      ))}
                    </div>
                  </div>

                  {/* Only on a real conversation. A voicemail does not produce a
                      date, and prompting for one there invites a guess that the
                      queue then treats as a commitment. */}
                  {callOutcome === 'Spoke with customer' && (
                    <div>
                      <div style={{ fontSize:11, color:textSub, textTransform:'uppercase', letterSpacing:'0.06em', marginBottom:6 }}>
                        Promise date <span style={{ textTransform:'none', letterSpacing:0, fontWeight:400 }}>— optional, if they said when</span>
                      </div>
                      <input type="date" value={callPromise} onChange={e => setCallPromise(e.target.value)}
                        style={{ background:inputBg, border:`1px solid ${inputBdr}`, borderRadius:7,
                          padding:'8px 12px', color:textPri, fontSize:13,
                          fontFamily:"'Inter',sans-serif", outline:'none' }} />
                    </div>
                  )}

                  <div style={{ display:'flex', flexDirection:'column', flex:1, minHeight:0 }}>
                    <div style={{ fontSize:11, color:textSub, textTransform:'uppercase', letterSpacing:'0.06em', marginBottom:6 }}>Note <span style={{ textTransform:'none', letterSpacing:0 }}>(optional)</span></div>
                    <textarea value={callNote} onChange={e => setCallNote(e.target.value)}
                      placeholder="Anything worth remembering next time…"
                      style={{ flex:1, minHeight:90, width:'100%', background:inputBg, border:`1px solid ${inputBdr}`,
                        borderRadius:7, padding:'10px 12px', color:textPri, fontSize:13,
                        fontFamily:"'Inter',sans-serif", outline:'none', resize:'vertical', boxSizing:'border-box', lineHeight:1.6 }} />
                  </div>

                  <button onClick={handleLogCall} disabled={loggingCall || !callOutcome}
                    style={{ padding:'11px', borderRadius:8, border:'none', fontSize:14, fontWeight:600,
                      cursor: loggingCall || !callOutcome ? 'not-allowed' : 'pointer',
                      fontFamily:"'Inter',sans-serif",
                      background: loggingCall || !callOutcome ? (isDark?'#243050':'#d0d0c8') : '#3498db',
                      color: loggingCall || !callOutcome ? textSub : '#fff' }}>
                    {loggingCall ? 'Logging…' : '📞 Log call'}
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Send Back expand panel */}
        {showSendBack && (
          <div style={{ padding:'16px 24px', borderTop:`1px solid ${border}`,
            background:isDark?'#1a1a0f':'#fffde7', flexShrink:0 }}>
            <div style={{ fontSize:13, fontWeight:600, color:'#e67e22', marginBottom:6 }}>
              Send this ticket back to the service queue?
            </div>
            <div style={{ fontSize:12, color:textSub, marginBottom:10 }}>
              Status will be changed to Open. Add an optional note to explain why.
            </div>
            <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:10, marginBottom:10 }}>
              <div>
                <div style={{ fontSize:11, color:textSub, marginBottom:5 }}>Reassign tech (optional)</div>
                <select value={sendBackTechId} onChange={e => setSendBackTechId(e.target.value)}
                  style={{ width:'100%', background:inputBg,
                    border:`1px solid ${sendBackTechId ? '#e67e22' : inputBdr}`,
                    borderRadius:6, padding:'8px 10px', color:textPri, fontSize:12,
                    fontFamily:"'Inter',sans-serif", outline:'none', cursor:'pointer' }}>
                  <option value="">— Keep current tech —</option>
                  {employees.map(e => <option key={e.id} value={e.id}>{e.name}</option>)}
                </select>
              </div>
              <div>
                <div style={{ fontSize:11, color:textSub, marginBottom:5 }}>Optional note</div>
                <textarea
                  value={sendBackNote}
                  onChange={e => setSendBackNote(e.target.value)}
                  placeholder="e.g. customer wants additional work"
                  rows={2}
                  autoFocus
                  style={{ width:'100%', background:inputBg, border:`1px solid ${inputBdr}`,
                    borderRadius:6, padding:'8px 10px', color:textPri, fontSize:12,
                    fontFamily:"'Inter',sans-serif", resize:'none', outline:'none', boxSizing:'border-box' }}
                />
              </div>
            </div>
            <div style={{ display:'flex', gap:8, justifyContent:'flex-end' }}>
              <button onClick={() => { setShowSendBack(false); setSendBackNote(''); setSendBackTechId(''); }}
                style={{ padding:'7px 16px', borderRadius:6, border:`1px solid ${border}`,
                  background:'transparent', color:textSub, cursor:'pointer', fontSize:12 }}>
                Cancel
              </button>
              <button onClick={handleSendBack} disabled={sendingBack}
                style={{ padding:'7px 20px', borderRadius:6, border:'none', background:'#e67e22',
                  color:'#fff', cursor:sendingBack?'not-allowed':'pointer', fontSize:12, fontWeight:600 }}>
                {sendingBack ? 'Sending back…' : 'Confirm Send Back'}
              </button>
            </div>
          </div>
        )}

        {/* Action bar */}
        <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between',
          padding:'14px 24px', borderTop:`1px solid ${border}`, background:panelBg, flexShrink:0 }}>
          <button onClick={() => setShowSendBack(!showSendBack)}
            style={{ padding:'9px 22px', borderRadius:8, border:`1px solid #e67e22`,
              background: showSendBack ? '#e67e22' : 'transparent',
              color: showSendBack ? '#fff' : '#e67e22',
              cursor:'pointer', fontSize:13, fontWeight:600, fontFamily:"'Inter',sans-serif" }}>
            ↩ Send Back
          </button>
          <div style={{ display:'flex', gap:10 }}>
            <button onClick={handlePickedUp} disabled={markingPickedUp}
              style={{ padding:'9px 28px', borderRadius:8, border:'none', background:'#2ecc71',
                color:'#fff', cursor:markingPickedUp?'not-allowed':'pointer', fontSize:13,
                fontWeight:600, fontFamily:"'Inter',sans-serif", opacity:markingPickedUp?0.7:1 }}>
              {markingPickedUp ? 'Logging…' : '✓ Picked Up'}
            </button>
            <button onClick={onClose}
              style={{ padding:'9px 20px', borderRadius:8, border:`1px solid ${border}`,
                background:'transparent', color:textSub, cursor:'pointer', fontSize:13,
                fontFamily:"'Inter',sans-serif" }}>
              Close
            </button>
          </div>
        </div>

        {/* Toast */}
        {toast && (
          <div style={{ position:'absolute', bottom:76, left:'50%', transform:'translateX(-50%)',
            padding:'10px 22px', borderRadius:8, fontSize:13, fontWeight:500,
            boxShadow:'0 4px 16px rgba(0,0,0,0.3)', zIndex:100, whiteSpace:'nowrap',
            background: toast.ok ? '#2ecc71' : '#e74c3c', color:'#fff' }}>
            {toast.ok ? '✓' : '⚠'} {toast.message}
          </div>
        )}

      </div>
    </div>
  );
}